import React, { useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { useAccount } from '../../contexts/account/AccountContexts';
import DropdownItem, { DropdownItemData } from '../../components/dropdown-item/DropdownItem';
import useChangeProjectKeyword from '../../hooks/filters/UseChangeProjectKeyword';
import { useGetCommonKeywordsWithTags } from '../../api/hooks/monitor/keyword/useGetCommonKeywordsWithTags';
import { getTagLabel } from '../../helpers/tags/TagsHelper';
import { useGetTags } from '../../api/hooks/tags/useGetTags';
import { useTranslation } from 'react-i18next';
import { TAG_NAME } from '../../utils/urls';
import CustomTooltip from '../common/tooltip/CustomTooltip';
import { Button } from 'primereact/button';
import { ProjectResponse } from '../../models/projects/ProjectResponse';

interface ChangeKeywordBoxProps {
  project: ProjectResponse;
  changeKeyword?: () => void;
}

const ChangeKeywordBox: React.FC<ChangeKeywordBoxProps> = ({ project, changeKeyword }) => {
  const { t } = useTranslation();
  const { accountResponse } = useAccount();

  const onRefresh = () => {
    reloadTags();
    reloadCommonKeywords();
    if (changeKeyword) {
      changeKeyword();
    }
  };

  const apiProjectId = project.id;
  const { tags, refetch: reloadTags } = useGetTags(apiProjectId);
  const { commonKeywords, refetch: reloadCommonKeywords } = useGetCommonKeywordsWithTags(apiProjectId);

  const { changeProjectKeyword } = useChangeProjectKeyword({
    onRefresh,
  });

  const projectId = project.id;

  const brand = useMemo(() => {
    if (!project) {
      return '';
    }
    return project.firmName || '';
  }, [project]);

  const defaultKeyword = project.defaultKeyword;

  const keywordsDataOptions: DropdownItemData<string>[] = useMemo(() => {
    const options = commonKeywords.map((data) => ({
      name: getTagLabel(tags, data.tagId, data.keyword),
      type: data.normalizedKeyword,
    }));

    const uniqueOptions = options.filter(
      (option, index, self) => index === self.findIndex((o) => o.type === option.type)
    );

    return uniqueOptions;
  }, [commonKeywords, tags]);

  if (!accountResponse || !project || !commonKeywords.length) {
    return <></>;
  }

  const defaultTagsData = tags.find((tr) =>
    tr.keywordsData.find((kd) => kd.normalizedKeyword === defaultKeyword)
  );

  const isResetDisabled =
    brand === defaultKeyword ||
    commonKeywords.length <= 1 ||
    brand === `${TAG_NAME}${defaultTagsData?.id}`;

  return (
    <div className="change-keyword-box">
      <Dropdown
        value={keywordsDataOptions.find((option) => option.type === brand) || null}
        onChange={(e) => changeProjectKeyword(projectId, e.value.type)}
        options={keywordsDataOptions}
        className={`dropdown`}
        optionLabel="name"
        itemTemplate={(option) => <DropdownItem name={option.name} />}
        filter
        panelClassName="dropdown__footer"
        panelFooterTemplate={
          <>
            <Button
              className={`btn btn--sm ${isResetDisabled ? 'btn--disabled-tooltip-color' : ''}`}
              id={`btn-default-name-${projectId}`}
              label={t('resetToDefault')}
              onClick={() => {
                if (isResetDisabled) {
                  return;
                }

                const newKeyword =
                  defaultTagsData && defaultTagsData.id
                    ? `${TAG_NAME}${defaultTagsData.id}`
                    : defaultKeyword;
                changeProjectKeyword(projectId, newKeyword);
              }}
            />
            {isResetDisabled && (
              <CustomTooltip
                tooltipText="defaultBrandAlreadyActive"
                targetRef={`btn-default-name-${projectId}`}
              />
            )}
          </>
        }
      />
    </div>
  );
};

export default ChangeKeywordBox;
